//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { IServiceProvider, ITimeoutService, IPromise, IRootScopeService, IAngularEvent } from 'angular'

//#endregion

//#region Helper Imports

import { TypeHelper, TemplateHelper, ConfigHelper } from '../../helpers'

//#endregion

//#region IMR Imports

import { ICON_SERVICE_NAME, IIMRIconProvider, IIMRIconConfiguration, IMRIconService } from '@imr/controls.angularjs/components/icon'

//#endregion

//#region Service Imports

import { $TIMEOUT_SERVICE_NAME, $ROOT_SCOPE_SERVICE_NAME } from '../system'
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'

//#endregion

//#region Public Types

export const UTILS_CONTROLS_SERVICE_NAME = 'UtilsControlsService'
export const UTILS_CONTROLS_SERVICE_PROVIDER_NAME = 'UtilsControlsServiceProvider'

//#endregion

//#region Click Helper

export class ClickHelper {

    //#region Members

    private clickTimer: IPromise<void>

    //#endregion

    //#region Ctor

    public constructor(private readonly $timeout: ITimeoutService) { }

    //#endregion

    //#region Click

    public click(clickFunc: () => void, dblClickFunc: () => void) {
        if (this.clickTimer != null) {
            this.clearTimer()

            if (dblClickFunc != null) {
                dblClickFunc()
            }
        }
        else {
            this.clickTimer = this.$timeout(() => {
                this.clearTimer()

                if (clickFunc != null) {
                    clickFunc()
                }
            }, 250)
        }
    }

    private clearTimer() {
        this.$timeout.cancel(this.clickTimer)
        this.clickTimer = null
    }

    //#endregion

    //#region Lifecycle

    public dispose(): void {
        this.clearTimer()
    }

    //#endregion
}

//#endregion

//#region Icons

export class NGTemplateProvider implements IIMRIconProvider {

    public constructor(private readonly icon: ConfigHelper.Icon, private readonly userSession: UserSessionService) { }

    public getTemplate(): string | null {
        return TemplateHelper.getIcon(this.icon, this.userSession)
    }
}

//#endregion

//#region Key Dispatcher

export class KeyDispatcher {

    //#region Consts

    private readonly KEY_DOWN_EVENT_NAME = 'KEY_DOWN_EVENT_NAME'
    private readonly EVENT_GUID: string = TypeHelper.guid()

    //#endregion

    //#region Members

    private registerdEvents: (() => void)[]

    //#endregion

    //#region Ctor

    public constructor(private readonly $rootScope: IRootScopeService) {
        this.registerdEvents = []

        document.addEventListener('keyup', (e) => {
            $rootScope.$broadcast(this.KEY_DOWN_EVENT_NAME + this.EVENT_GUID, e.keyCode)
        })
    }

    //#endregion

    //#region Lifecycle

    public dispose() {
        _.forEach(this.registerdEvents, (deregisterEvent: () => void) => {
            deregisterEvent()
        })
    }

    //#endregion

    //#region Key Events

    public onKeyDown(keyCode: KeyDispatcher.KeyCode, func: () => void): () => void {
        const deregisterFunc = this.$rootScope.$on(this.KEY_DOWN_EVENT_NAME + this.EVENT_GUID, (event: IAngularEvent, eventKeyCode: KeyDispatcher.KeyCode) => {
            if (keyCode === eventKeyCode) {
                func()
            }
        })

        this.registerdEvents.push(deregisterFunc)

        return deregisterFunc
    }

    //#endregion
}

export module KeyDispatcher {
    export enum KeyCode {
        ESC = 27,
    }
}

//#endregion

export class UtilsControlsService {

    //#region Ctor

    public constructor(
        private readonly $rootScope: IRootScopeService,
        private readonly $timeout: ITimeoutService,
        private readonly iconService: IMRIconService,
        private readonly userSessionService: UserSessionService
    ) { }

    //#endregion

    //#region Click

    public getClickHelper(): ClickHelper {
        return new ClickHelper(this.$timeout)
    }

    //#endregion

    //#region Icon

    public getIconProvider(icon: ConfigHelper.Icon): IIMRIconConfiguration {
        return this.iconService.createIcon(ConfigHelper.Icon[icon].toLowerCase(), new NGTemplateProvider(icon, this.userSessionService))
    }

    //#endregion

    //#region Key

    public getKeyDispatcher(): KeyDispatcher {
        return new KeyDispatcher(this.$rootScope)
    }

    //#endregion
}

export class UtilsControlsServiceProvider implements IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        $ROOT_SCOPE_SERVICE_NAME,
        $TIMEOUT_SERVICE_NAME,
        ICON_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        (
            $rootScope: IRootScopeService,
            $timeout: ITimeoutService,
            iconService: IMRIconService,
            userSessionService: UserSessionService
        ) => {
            return new UtilsControlsService($rootScope, $timeout, iconService, userSessionService)
        }]

    //#endregion
} 